'use client';

import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { Location, getTimezoneFromCoords } from '@/lib/eclipse-utils';
import { useEclipseData } from '@/hooks/useEclipseData';

export function EclipseCountdownCompact() {
  const router = useRouter();
  const [userLocation, setUserLocation] = useState<Location | null>(null);
  const [isClient, setIsClient] = useState(false);

  const { nextEclipse, eclipseInfo, timeLeft } = useEclipseData(userLocation, 0);

  // Set default location (Spain) and optionally detect user location if permission already granted
  useEffect(() => {
    if (!isClient) return;

    // Default location: Madrid, Spain (good for European eclipses)
    const defaultLocation: Location = {
      lat: 40.4168,
      lng: -3.7038,
      name: 'Madrid, Spain',
      timezone: 'Europe/Madrid'
    };

    // Set default location immediately
    setUserLocation(defaultLocation);

    const detectLocationIfPermitted = async () => {
      try {
        if ('geolocation' in navigator && 'permissions' in navigator) {
          // Check if we already have permission
          const permission = await navigator.permissions.query({ name: 'geolocation' });

          if (permission.state === 'granted') {
            // Only request location if permission is already granted
            navigator.geolocation.getCurrentPosition(
              async (position) => {
                const { latitude, longitude } = position.coords;

                const detectedLocation = {
                  lat: latitude,
                  lng: longitude,
                  name: `${latitude.toFixed(2)}, ${longitude.toFixed(2)}`,
                  timezone: getTimezoneFromCoords(latitude, longitude)
                };

                setUserLocation(detectedLocation);
              },
              (error) => {
                console.log('Geolocation error:', error);
                // Keep default location
              },
              {
                enableHighAccuracy: false,
                timeout: 5000,
                maximumAge: 600000 // 10 minutes
              }
            );
          }
          // If permission is 'denied' or 'prompt', we keep the default location
        }
      } catch (error) {
        console.log('Location detection error:', error);
        // Keep default location
      }
    };

    detectLocationIfPermitted();
  }, [isClient]);

  useEffect(() => {
    setIsClient(true);
  }, []);

  const handleClick = () => {
    router.push('/countdown');
  };

  // Don't render until client-side
  if (!isClient || !nextEclipse) {
    return null;
  }

  // If eclipse is visible from user location, show countdown
  if (eclipseInfo?.visible && timeLeft.total > 0) {
    return (
      <button
        onClick={handleClick}
        className="group flex items-center gap-2 px-3 py-1.5 rounded-md hover:bg-muted/50 transition-colors cursor-pointer"
      >

        {/* Countdown Display */}
        <div className="flex items-baseline gap-1 font-mono text-sm">
          <span className="font-medium">{timeLeft.days}</span>
          <span className="text-xs text-muted-foreground">d</span>
          <span className="font-medium">{timeLeft.hours.toString().padStart(2, '0')}</span>
          <span className="text-xs text-muted-foreground">h</span>
          <span className="font-medium">{timeLeft.minutes.toString().padStart(2, '0')}</span>
          <span className="text-xs text-muted-foreground">m</span>
        </div>
      </button>
    );
  }

  // If eclipse is not visible or no location, show minimal link
  return (
    <button
      onClick={handleClick}
      className="flex items-center gap-2 px-3 py-1.5 rounded-md hover:bg-muted/50 transition-colors cursor-pointer text-muted-foreground"
    >
      {/* Eclipse Icon */}
      <div className="w-4 h-4 rounded-full border border-current opacity-50 flex-shrink-0" />

      {/* Text */}
      <span className="text-sm">Eclipse</span>
    </button>
  );
}
